/**
 * Main React UI for cleaner graph visualization
 */

import '@xyflow/react/dist/style.css';
import './styles/global.css';

import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { SessionList } from './components/SessionList';
import { ProjectBrowser } from './components/ProjectBrowser';
import { Header } from './components/Header';
import { GraphCanvas } from './components/graph/GraphCanvas';
import { ErrorOverlay } from './components/ErrorOverlay';

import { AppStateProvider, useAppState } from './contexts/AppContext';
import { AppInitProvider, useAppInit } from './contexts/AppInit';
import { ScopeProvider } from './contexts/ScopeContext';
import { PaletteProvider } from './palettes/PaletteContext';
import { styles } from './App.styles';
import { ReactFlowProvider } from '@xyflow/react';
import { RuntimeProvider } from './contexts/RuntimeProvider';
import { NodeInspector } from './components/NodeInspector';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ProjectLoader } from './components/ProjectLoader';
import { InfoToast } from './components/InfoToast';

function RedirectToNewDraft() {
  const { sessionId } = useParams();
  const draftId = crypto.randomUUID().slice(0, 8);
  return <Navigate to={`/${sessionId}/new/${draftId}`} replace />;
}

function MainView() {
  const { inspecting } = useAppState();

  return (
    <div style={styles.container}>
      <RuntimeProvider>
        <ScopeProvider>
          <Header />
          <InfoToast />
          <ReactFlowProvider>
            <ErrorBoundary>
              <GraphCanvas />
            </ErrorBoundary>
          </ReactFlowProvider>
        </ScopeProvider>

        {inspecting && <NodeInspector />}
      </RuntimeProvider>

      <ProjectBrowser />
      <ErrorOverlay />
    </div>
  );
}

function AppContent() {
  const { loading, error } = useAppInit();

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.message}>Connecting to server...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>
          <div style={{ fontSize: 18, marginBottom: 10 }}>Connection Error</div>
          <div>{error}</div>
          <div style={{ marginTop: 20, fontSize: 12, color: '#9ca3af' }}>
            Make sure the REPL is running with the server enabled.
          </div>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Root - show session list */}
      <Route path="/" element={<SessionList />} />

      {/* Session only - redirect to new draft */}
      <Route path="/:sessionId" element={<RedirectToNewDraft />} />

      {/* /new without GUID - redirect to /new/{guid} */}
      <Route path="/:sessionId/new" element={<RedirectToNewDraft />} />

      {/* New draft project */}
      <Route
        path="/:sessionId/new/:draftId/*"
        element={
          <ProjectLoader>
            <MainView />
          </ProjectLoader>
        }
      />

      {/* Saved project */}
      <Route
        path="/:sessionId/project/:projectName/*"
        element={
          <ProjectLoader>
            <MainView />
          </ProjectLoader>
        }
      />
    </Routes>
  );
}

export function App() {
  return (
    <AppInitProvider>
      <AppStateProvider>
        <PaletteProvider>
          <AppContent />
        </PaletteProvider>
      </AppStateProvider>
    </AppInitProvider>
  );
}
